import { Link } from 'react-router-dom';
import LandingNav from '../components/LandingNav';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';
import { COMPARISONS } from '../data/comparisons';

const BASE_URL = 'https://momentsent.com';

/**
 * Comparison hub: /compare
 *
 * Lists every head-to-head comparison page in one place. Serves two purposes:
 *   1. A human entry point to the /vs/:slug pages (linked from the footer).
 *   2. An SEO asset: it can rank for "card mailing services compared" style
 *      searches and gives crawlers clean internal links to all comparisons.
 */
export default function ComparisonHubPage() {
  const canonical = `${BASE_URL}/compare`;

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'MomentSent compared to other card mailing services',
      itemListElement: COMPARISONS.map((c, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: c.h1,
        url: `${BASE_URL}/vs/${c.slug}`,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'Compare', item: canonical },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <PageSEO
        title="MomentSent vs Other Card Mailing Services | Compare"
        description="See how MomentSent compares to other card mailing services on pricing, automation, design selection, and real folded cards mailed in an envelope. Honest side-by-side comparisons."
        canonical={canonical}
        structuredData={structuredData}
      />
      <LandingNav />

      {/* ── Hero ────────────────────────────────────────────────────────────── */}
      <section className="pt-28 pb-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-body text-sm text-sage font-semibold tracking-widest uppercase mb-4">
            Compare
          </p>
          <h1 className="font-heading text-4xl md:text-5xl text-navy leading-tight mb-6">
            How MomentSent compares
          </h1>
          <p className="font-body text-lg text-navy/80 leading-relaxed">
            There are a lot of ways to send a card by mail. Here is how MomentSent stacks up against
            other services on pricing, automation, design selection, and real folded cards mailed in
            an envelope. Each comparison is honest, including where the other service may fit you better.
          </p>
        </div>
      </section>

      {/* ── Comparison cards ────────────────────────────────────────────────── */}
      <section className="pb-12 px-6">
        <div className="max-w-3xl mx-auto grid sm:grid-cols-2 gap-5">
          {COMPARISONS.map((c) => (
            <Link
              key={c.slug}
              to={`/vs/${c.slug}`}
              className="group bg-white rounded-card p-6 shadow-warm border border-gray-100
                         hover:shadow-warm-lg hover:border-sage/40 transition-all"
            >
              <h2 className="font-heading text-xl text-navy mb-2 group-hover:text-sage transition-colors">
                MomentSent vs {c.competitor}
              </h2>
              <p className="font-body text-sm text-gray-500 leading-relaxed mb-4">
                {c.bestFor.competitor}
              </p>
              <span className="font-body text-sm text-sage font-semibold">
                See the comparison →
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────────── */}
      <section className="py-20 px-6 bg-sage/10 mt-auto">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="font-heading text-3xl text-navy mb-4">
            Ready to never miss a moment?
          </h2>
          <p className="font-body text-base text-gray-500 leading-relaxed mb-8">
            Set the dates once and MomentSent mails a real card automatically, every year, to the
            people you love.
          </p>
          <Link to="/signup" className="btn-primary inline-block px-8 py-3 text-base">
            Start your free trial →
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
